import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';
import {
  Loader2, Type, Image as ImageIcon, QrCode, Square, Trash2, Download,
  Save, Plus, ChevronUp, ChevronDown, Users, Palette, Share2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT_CSS = { unbounded: "'Unbounded', sans-serif", space: "'Space Grotesk', sans-serif" };
const PREVIEW_W = 420;
const uid = () => `el_${Math.random().toString(36).slice(2, 10)}`;

export default function FlyerCreator({ eventId, token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [event, setEvent] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [size, setSize] = useState('classic');
  const [design, setDesign] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [current, setCurrent] = useState(null); // saved design doc being edited
  const [name, setName] = useState('My flyer');
  const [share, setShare] = useState(true);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const fileRef = useRef(null);

  const scale = design ? PREVIEW_W / design.width : 1;

  const loadDesigns = useCallback(() => {
    axios.get(`${API}/events/${eventId}/flyer-designs`, auth).then(r => setDesigns(r.data.designs || [])).catch(() => {});
    // eslint-disable-next-line
  }, [eventId]);

  useEffect(() => {
    axios.get(`${API}/hosted-events/${eventId}`, auth).then(r => setEvent(r.data?.event || r.data)).catch(() => {});
    loadDesigns();
    loadTemplate('classic');
    // eslint-disable-next-line
  }, [eventId]);

  const loadTemplate = async (sz) => {
    try {
      const r = await axios.get(`${API}/events/${eventId}/flyer-template`, { ...auth, params: { size: sz } });
      setDesign(r.data.design); setSizes(r.data.sizes); setSize(sz); setCurrent(null); setSelId(null);
    } catch { toast.error('Could not load template'); }
  };

  const sel = design?.elements?.find(e => e.id === selId) || null;
  const patchEl = (id, patch) => setDesign(d => ({ ...d, elements: d.elements.map(e => e.id === id ? { ...e, ...patch } : e) }));
  const patchBg = (patch) => setDesign(d => ({ ...d, background: { ...d.background, ...patch } }));

  const addEl = (el) => {
    const ne = { id: uid(), x: 60, y: 60, ...el };
    setDesign(d => ({ ...d, elements: [...d.elements, ne] }));
    setSelId(ne.id);
  };
  const removeEl = (id) => { setDesign(d => ({ ...d, elements: d.elements.filter(e => e.id !== id) })); setSelId(null); };
  const moveLayer = (id, dir) => setDesign(d => {
    const i = d.elements.findIndex(e => e.id === id);
    const j = i + dir;
    if (j < 0 || j >= d.elements.length) return d;
    const els = [...d.elements];
    [els[i], els[j]] = [els[j], els[i]];
    return { ...d, elements: els };
  });

  const onDown = (e, el) => {
    e.preventDefault(); e.stopPropagation();
    setSelId(el.id);
    dragRef.current = { id: el.id, sx: e.clientX, sy: e.clientY, ox: el.x, oy: el.y };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    patchEl(d.id, { x: Math.round(d.ox + (e.clientX - d.sx) / scale), y: Math.round(d.oy + (e.clientY - d.sy) / scale) });
  };
  const onUp = () => { dragRef.current = null; };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, auth);
      const url = `${API}/files/${r.data.path}`;
      if (fileRef.current?.target === 'bg') patchBg({ image_url: url });
      else if (sel && sel.type === 'image') patchEl(sel.id, { src: url });
      else addEl({ type: 'image', w: 240, h: 240, src: url });
      toast.success('Image added');
    } catch { toast.error('Upload failed'); }
  };

  const save = async (asNew = false) => {
    setBusy(true);
    try {
      if (current && !asNew) {
        await axios.put(`${API}/flyer-designs/${current.design_id}`, { name, design, share_to_street_team: share }, auth);
        toast.success('Flyer saved');
      } else {
        const r = await axios.post(`${API}/events/${eventId}/flyer-designs`, { name, design, share_to_street_team: share }, auth);
        setCurrent(r.data);
        toast.success(share ? 'Flyer saved & shared with your street team' : 'Flyer saved');
      }
      loadDesigns();
    } catch (e2) { toast.error(e2.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    if (!current) { toast.error('Save the flyer first, then download'); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/flyer-designs/${current.design_id}`, { name, design, share_to_street_team: share }, auth);
      const r = await axios.get(`${API}/flyer-designs/${current.design_id}/render`, { ...auth, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${name}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Render failed'); }
    finally { setBusy(false); }
  };

  const openDesign = (d) => {
    setCurrent(d); setDesign(d.design); setName(d.name);
    setShare(!!d.share_to_street_team); setSize(d.design?.size || 'classic'); setSelId(null);
  };

  const performers = (event?.performers || []).filter(p => p?.images?.length);

  if (!design) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="flyer-creator">
      <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={uploadImage} />
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={e => setName(e.target.value)} className="w-48" data-testid="flyer-name" />
        <select value={size} onChange={e => loadTemplate(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="flyer-size-select">
          {sizes.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs border border-border rounded-full px-3 py-1.5">
          <Share2 className="h-3.5 w-3.5 text-fuchsia-400" /> Share with street team
          <Switch checked={share} onCheckedChange={setShare} data-testid="flyer-share-toggle" />
        </label>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => save(true)} disabled={busy} data-testid="flyer-save-new">Save as new</Button>
        <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" onClick={() => save(false)} disabled={busy} data-testid="flyer-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={download} disabled={busy} data-testid="flyer-download">
          <Download className="h-4 w-4 mr-1" /> PNG
        </Button>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,460px)_1fr] gap-4">
        {/* Canvas */}
        <div>
          <div ref={canvasRef} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
            className="relative overflow-hidden rounded-xl border border-border select-none mx-auto"
            style={{
              width: PREVIEW_W, height: design.height * scale,
              backgroundColor: design.background?.color || '#0b0f1f',
              backgroundImage: design.background?.image_url ? `url(${design.background.image_url})` : undefined,
              backgroundSize: 'cover', backgroundPosition: 'center',
            }}
            onMouseDown={() => setSelId(null)}
            data-testid="flyer-canvas">
            {design.background?.overlay > 0 && (
              <div className="absolute inset-0" style={{ backgroundColor: design.background.color || '#0b0f1f', opacity: design.background.overlay }} />
            )}
            {design.elements.map(el => {
              const st = { position: 'absolute', left: el.x * scale, top: el.y * scale, width: el.w * scale, height: el.h * scale, cursor: 'move' };
              const ring = selId === el.id ? 'ring-2 ring-fuchsia-500' : '';
              if (el.type === 'text') return (
                <div key={el.id} onMouseDown={(e) => onDown(e, el)} className={ring}
                  style={{ ...st, color: el.color, fontFamily: FONT_CSS[el.font] || FONT_CSS.space, fontSize: el.size * scale, fontWeight: el.bold ? 700 : 500, lineHeight: 1.22, textAlign: el.align || 'left', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
                  {el.text}
                </div>
              );
              if (el.type === 'image') return (
                <img key={el.id} src={el.src} alt="" onMouseDown={(e) => onDown(e, el)} draggable={false}
                  className={`object-cover ${ring}`} style={{ ...st, borderRadius: el.circle ? '50%' : (el.radius || 0) * scale }} />
              );
              if (el.type === 'qr') return (
                <div key={el.id} onMouseDown={(e) => onDown(e, el)} className={`bg-white rounded-lg flex items-center justify-center ${ring}`} style={st}>
                  <QrCode className="text-black" style={{ width: '72%', height: '72%' }} />
                </div>
              );
              return (
                <div key={el.id} onMouseDown={(e) => onDown(e, el)} className={ring}
                  style={{ ...st, backgroundColor: el.fill, borderRadius: (el.radius || 0) * scale }} />
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground text-center mt-1.5">Drag elements to position · preview is approximate, download for the final PNG</p>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <Card className="border-border"><CardContent className="py-3 space-y-2">
            <p className="text-xs font-semibold flex items-center gap-1.5"><Plus className="h-3.5 w-3.5 text-fuchsia-400" /> Add elements</p>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="add-text"
                onClick={() => addEl({ type: 'text', w: 500, h: 80, text: 'Your text here', font: 'space', size: 36, color: '#ffffff' })}>
                <Type className="h-3.5 w-3.5 mr-1" /> Text
              </Button>
              {event?.image_url && (
                <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="add-event-image"
                  onClick={() => addEl({ type: 'image', w: 400, h: 260, src: event.image_url })}>
                  <ImageIcon className="h-3.5 w-3.5 mr-1" /> Event image
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="add-upload"
                onClick={() => { fileRef.current.target = 'el'; fileRef.current.click(); }}>
                <ImageIcon className="h-3.5 w-3.5 mr-1" /> Upload / logo
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="add-qr"
                onClick={() => addEl({ type: 'qr', w: 240, h: 240 })}>
                <QrCode className="h-3.5 w-3.5 mr-1" /> QR code
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="add-box"
                onClick={() => addEl({ type: 'rect', w: 300, h: 120, fill: '#8b5cf6', radius: 16 })}>
                <Square className="h-3.5 w-3.5 mr-1" /> Box
              </Button>
            </div>
            {performers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {performers.slice(0, 6).map((p, i) => (
                  <button key={i} onClick={() => addEl({ type: 'image', w: 120, h: 120, src: p.images[0], circle: true })}
                    className="flex items-center gap-1.5 text-xs border border-border rounded-full pl-1 pr-2.5 py-1 hover:border-fuchsia-500/60"
                    data-testid={`add-performer-${i}`}>
                    <img src={p.images[0]} alt="" className="h-5 w-5 rounded-full object-cover" /> {p.name?.slice(0, 14)}
                  </button>
                ))}
              </div>
            )}
          </CardContent></Card>

          <Card className="border-border"><CardContent className="py-3 space-y-2">
            <p className="text-xs font-semibold flex items-center gap-1.5"><Palette className="h-3.5 w-3.5 text-fuchsia-400" /> Background</p>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <input type="color" value={design.background?.color || '#0b0f1f'} onChange={e => patchBg({ color: e.target.value })}
                className="h-8 w-10 rounded border border-border bg-transparent" data-testid="bg-color" />
              {event?.image_url && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => patchBg({ image_url: event.image_url, overlay: 0.6 })}>Use event image</Button>}
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { fileRef.current.target = 'bg'; fileRef.current.click(); }}>Upload</Button>
              {design.background?.image_url && (
                <>
                  <Button size="sm" variant="outline" className="h-8 text-xs text-rose-400" onClick={() => patchBg({ image_url: null, overlay: 0 })}>Remove image</Button>
                  <label className="flex items-center gap-1.5">Dim <input type="range" min="0" max="0.9" step="0.05" value={design.background?.overlay || 0}
                    onChange={e => patchBg({ overlay: parseFloat(e.target.value) })} /></label>
                </>
              )}
            </div>
          </CardContent></Card>

          {sel && (
            <Card className="border-fuchsia-500/40"><CardContent className="py-3 space-y-2" data-testid="element-inspector">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold capitalize">{sel.type} element</p>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moveLayer(sel.id, -1)}><ChevronDown className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moveLayer(sel.id, 1)}><ChevronUp className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-400" onClick={() => removeEl(sel.id)} data-testid="delete-element"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              {sel.type === 'text' && (
                <>
                  <Textarea rows={2} value={sel.text} onChange={e => patchEl(sel.id, { text: e.target.value })} data-testid="text-content" />
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <select value={sel.font} onChange={e => patchEl(sel.id, { font: e.target.value })}
                      className="h-8 rounded-md border border-border bg-background px-2" data-testid="text-font">
                      <option value="unbounded">Unbounded (display)</option>
                      <option value="space">Space Grotesk</option>
                    </select>
                    <Input type="number" className="w-20 h-8" value={sel.size} onChange={e => patchEl(sel.id, { size: parseInt(e.target.value) || 12 })} />
                    <input type="color" value={sel.color} onChange={e => patchEl(sel.id, { color: e.target.value })} className="h-8 w-10 rounded border border-border bg-transparent" />
                    <Button size="sm" variant={sel.bold ? 'default' : 'outline'} className="h-8 text-xs font-bold" onClick={() => patchEl(sel.id, { bold: !sel.bold })}>B</Button>
                    <select value={sel.align || 'left'} onChange={e => patchEl(sel.id, { align: e.target.value })} className="h-8 rounded-md border border-border bg-background px-2">
                      <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                    </select>
                  </div>
                </>
              )}
              {sel.type === 'image' && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { fileRef.current.target = 'el'; fileRef.current.click(); }}>Replace image</Button>
                  <Button size="sm" variant={sel.circle ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => patchEl(sel.id, { circle: !sel.circle })}>Circle</Button>
                </div>
              )}
              {sel.type === 'rect' && (
                <div className="flex items-center gap-2 text-xs">
                  <input type="color" value={sel.fill} onChange={e => patchEl(sel.id, { fill: e.target.value })} className="h-8 w-10 rounded border border-border bg-transparent" />
                  <label className="flex items-center gap-1.5">Corners <input type="range" min="0" max="80" value={sel.radius || 0} onChange={e => patchEl(sel.id, { radius: parseInt(e.target.value) })} /></label>
                </div>
              )}
              <div className="grid grid-cols-4 gap-1.5">
                {['x', 'y', 'w', 'h'].map(k => (
                  <div key={k}><label className="text-[10px] text-muted-foreground uppercase">{k}</label>
                    <Input type="number" className="h-8" value={sel[k]} onChange={e => patchEl(sel.id, { [k]: parseInt(e.target.value) || 0 })} data-testid={`el-${k}`} /></div>
                ))}
              </div>
            </CardContent></Card>
          )}

          <Card className="border-border"><CardContent className="py-3 space-y-2">
            <p className="text-xs font-semibold flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-fuchsia-400" /> Saved flyers</p>
            {designs.length === 0 ? <p className="text-xs text-muted-foreground">Nothing saved yet — hit Save and it shows up here (and for your street team if sharing is on).</p> : (
              <div className="space-y-1.5">
                {designs.map(d => (
                  <div key={d.design_id} className="flex items-center gap-2 text-xs">
                    <button onClick={() => openDesign(d)} className={`flex-1 text-left truncate hover:text-fuchsia-300 ${current?.design_id === d.design_id ? 'text-fuchsia-300 font-semibold' : ''}`}
                      data-testid={`open-design-${d.design_id}`}>
                      {d.name} <span className="text-muted-foreground">· {d.design?.width}×{d.design?.height}{d.share_to_street_team ? ' · shared' : ''}</span>
                    </button>
                    <button onClick={async () => { await axios.delete(`${API}/flyer-designs/${d.design_id}`, auth); loadDesigns(); if (current?.design_id === d.design_id) setCurrent(null); }}
                      className="text-rose-400 hover:text-rose-300" data-testid={`del-design-${d.design_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </CardContent></Card>
        </div>
      </div>
    </div>
  );
}
